import { createUUID } from "mocks/data/utils"
import ajax from "plugins/AjaxService"
import { AttachableType, Attachment, AttachmentBulkPayload } from "types/Attachment"
import humps from "humps";



/**
 * INDEX
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-60655
 */
async function index(farmId: number, cycleUuid?: string): Promise<{ data: Attachment[] }> {
    const query = cycleUuid ? `?cycle_uuid=${cycleUuid}` : ``
    return ajax.get(`farms/${farmId}/attachments${query}`)
}
async function indexEntity(farmId: number, id: number, type: string): Promise<{ data: Attachment[] }> {
    return ajax.get(`farms/${farmId}/attachments?id=${id}&type=${type}`)
}

/**
 * GET
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-60695
 */
async function get(farmId: number, id: number): Promise<{ data: Attachment }> {
    return ajax.get(`farms/${farmId}/attachments/${id}`)
}

/**
 * DOWNLOAD
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-60715
 */
async function download(farmId: number, id: number): Promise<Blob> {
    return ajax.get(`farms/${farmId}/attachments/${id}/download`, null, { blob: true })
}
const thumbCache = new Map<string, Blob>()
const CACHE_LIMIT = 50

/**
 * DOWNLOAD PREVIEW
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-60715
 */
async function downloadThumb(farmId: number, id: number): Promise<Blob> {
    const key = `${farmId}-${id}`

    // Check cache
    if (thumbCache.has(key)) {
        const cached = thumbCache.get(key)!
        // refresh LRU
        thumbCache.delete(key)
        thumbCache.set(key, cached)
        return cached
    }

    // Fetch
    const blob = await ajax.get(`farms/${farmId}/attachments/${id}/download_preview`, null, { blob: true, noBusy: true })

    // Store in cache
    if (thumbCache.size >= CACHE_LIMIT) {
        // remove oldest
        const oldest = thumbCache.keys().next().value
        if (oldest) thumbCache.delete(oldest)
    }
    thumbCache.set(key, blob)

    return blob
}


/**
 * BULK MANAGE
 * https://app.clickup.com/30373227/v/dc/wyxbb-20075/wyxbb-62175
 * Manage differences between old and new attachments
 */
async function manageDiff(
    farmId: number,
    attachableId: number,
    attachableType: AttachableType,
    oldAttachments: Attachment[],
    newAttachments: Attachment[],
    growUnitId?: number,
    cropLotId?: number,
    cycleId?: number,
): Promise<void> {

    const toDelete = oldAttachments
        .filter(old => !!old.id && !newAttachments.some(curr => curr.id === old.id))
        .map(curr => curr.id!)
    const toAdd = newAttachments.filter(curr => !curr.id && curr.file)
        .map(curr => {
            curr["filename"] = `${createUUID()}${curr.name.substring(curr.name.lastIndexOf('.'))}`
            return curr
        })
    const toModify = newAttachments.filter(curr => {
        if ( !curr.id ) return false
        const old = oldAttachments.find(o => o.id === curr.id)
        if ( !old ) return false
        if ( curr.layer?.layerId !== old?.layer?.layerId ) return true
        const currLayers = curr.layer?.trolleysPosition || []
        const oldLayers = old.layer?.trolleysPosition || []
        if (currLayers.sort().toString() != oldLayers.sort().toString()) return true
        return false        
    })
    // if no changes, skip
    if (toDelete.length == 0 && toAdd.length == 0 && toModify.length == 0) return

    const attachmentsToAdd = toAdd.map<Partial<Attachment>>(a => ({
        filename: a["filename"],
        contentType: a.contentType,
        cycleLayerId: a.layer?.layerId,
        trolleyPosition: a.layer?.trolleysPosition,
    }))
    const attachmentsToModify = toModify.map<Partial<Attachment>>(a => ({
        id: a.id,
        cycleLayerId: a.layer?.layerId,
        trolleyPosition: a.layer?.trolleysPosition,
    }))
    const payload: AttachmentBulkPayload = {
        attachableId,
        attachableType,
        growUnitId,
        cropLotId,
        cycleId,
        attachments: [...attachmentsToAdd, ...attachmentsToModify],
        toDelete,
    }

    const formData = new FormData();
    formData.append('json', JSON.stringify(humps.decamelizeKeys(payload)))
    toAdd.forEach((attach, index) => {
        formData.append(attach["filename"], attach.file)
    })

    return ajax.post(`farms/${farmId}/attachments/bulk_manage`, formData)
}




const attachApi = {
    index,
    indexEntity,
    get,
    download,
    downloadThumb,
    manageDiff,
}
export default attachApi


